import { getYear } from "date-fns";
import { Fragment } from "react";
import { Grid, Box } from "@mui/material";
import { CategoryContainer } from "@/features/mypage/components/CategoryContainer";
import { MenuButton } from "@/features/mypage/components/MenuButton";
import { getMenuData } from "@/configs/menuDataDefinition";
import { NewsSummaryContainer } from "@/features/mypage/components/NewsSummaryContainer";
import { useFeatureFlags } from "@/configs/featureFlag";
import { useCorporateMasterEnabled } from "@/hooks/useCorporateMasterEnabled";
import { AiStoredPicturesChat } from "@/features/aiStoredPictures";
import { MonthlyMemberSection } from "@/features/mypage/components/MonthlyMemberSection";
import { useIsMonthlyMember } from "@/features/brandBadge";
import { RegistryChangeAlertSummarySection } from "@/features/registryChangeAlert";
import { MypageTopBanner } from "@/features/mypage/components/MypageTopBanner";
import { FloatingBanner } from "@/features/banner/components/FloatingBanner";
import { ReassistServiceCard } from "@/features/reassistRequests";
import { useLogin } from "@/hooks/useLogin";

export const MyPage: React.FC = () => {
  const { isLoggedIn } = useLogin();
  const currentYear = getYear(Date.now());
  const isMonthlyMember = useIsMonthlyMember();

  const {
    realEstateRegistrationAggregationOfNames,
    realEstateRegistrationMonitoring,
    commercialBookList,
    commercialCorporationList,
    commercialRegistrationReceptionBook,
    commercialRegistrationAggregationOfNames,
    commercialRegistrationMonitoring,
    commercialPictureAnalysis,
    allMattersAnalysis,
    dmManagement,
    emailManagement,
    customerManagement,
    ownerInfoList,
    ownerInfoViaScraping,
    realEstateTitle,
    rightsSectionOtsukuSearch,
    aiChat,
    aiManualSearch,
    inheritanceReuseCandidates,
    registryChangeAlert,
    realEstateResearchRequest,
  } = useFeatureFlags();
  const corporateMasterEnabled = useCorporateMasterEnabled();

  // NOTE: フラグオン(true)は〈表示する〉なのだが、引数はDisable（非表示）かどうかなので逆転している
  const menuData = getMenuData({
    realEstateRegistrationAggregationOfNames:
      !realEstateRegistrationAggregationOfNames,
    commercialBookList: !commercialBookList,
    corporateMaster: !corporateMasterEnabled,
    commercialCorporationList: !!commercialCorporationList,
    commercialRegistrationReceptionBook: !commercialRegistrationReceptionBook,
    realEstateRegistrationMonitoring: !realEstateRegistrationMonitoring,
    commercialRegistrationAggregationOfNames:
      !commercialRegistrationAggregationOfNames,
    commercialRegistrationMonitoring: !commercialRegistrationMonitoring,
    commercialPictureAnalysis: !commercialPictureAnalysis,
    allMattersAnalysis: !allMattersAnalysis,
    dmManagement: !dmManagement,
    emailManagement: !emailManagement,
    customerManagement: !customerManagement,
    ownerInfoList: !ownerInfoList,
    ownerInfoViaScraping: !ownerInfoViaScraping,
    realEstateTitle: !realEstateTitle,
    rightsSectionOtsukuSearch: !rightsSectionOtsukuSearch,
    realEstateResearchRequest: realEstateResearchRequest !== true,
  });

  return (
    <Box
      sx={{
        p: 3,
        backgroundColor: "background.default",
        display: "flex",
        flexDirection: "column",
        gap: 3,
      }}
    >
      {/* ピックアップ（ピン留めお知らせ最大2件＋広告カード）。 */}
      <MypageTopBanner year={currentYear} />
      <RegistryChangeAlertSummarySection
        enabled={registryChangeAlert === true}
      />
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
        {menuData.map((category) => (
          <Fragment key={`mypage-${category.title}`}>
            <CategoryContainer
              level={category.level}
              title={category.title}
              icon={category?.icon}
            >
              <Grid container rowSpacing={2} columnSpacing={2}>
                {category.menus.map((menu) => {
                  return (
                    <Grid
                      item
                      key={`mypage-${category.title}-grid-${menu.label}`}
                      xs={12}
                      sm={6}
                      md={3}
                      lg={2}
                      xl={2}
                    >
                      <MenuButton
                        key={`mypage-${category.title}-${menu.label}`}
                        label={menu.label}
                        variant={menu.variant}
                        href={menu?.href}
                        description={menu?.description}
                        disabled={menu?.disabled}
                        icon={menu?.icon}
                        actionType={menu.actionType}
                      />
                    </Grid>
                  );
                })}
              </Grid>
            </CategoryContainer>
            {/* 「不動産登記データ化」ブロックの下に月額会員様限定セクションを配置。
                月額会員 かつ 機能フラグ有効時のみ表示（非会員には出さない）。 */}
            {category.title === "不動産登記データ化" &&
              isMonthlyMember &&
              inheritanceReuseCandidates === true && (
                <Box sx={{ mt: 3, mb: 1 }}>
                  <MonthlyMemberSection />
                </Box>
              )}
          </Fragment>
        ))}
      </Box>
      {/* R.E.ASSIST（役所資料の取得代行）は全幅のサービスカード。「その他」の下、新着情報の上に置く */}
      <ReassistServiceCard />
      <NewsSummaryContainer title={"新着情報"} year={currentYear} />
      {isLoggedIn && (aiChat === true || aiManualSearch === true) && (
        <AiStoredPicturesChat
          isAiChatEnabled={aiChat === true}
          isManualSearchEnabled={aiManualSearch === true}
        />
      )}
      {/* 右下フローティング広告（マイページ限定） */}
      <FloatingBanner />
    </Box>
  );
};
